/* global routes, fetchConfig */
// ====================================================================
// product-compare.js  —  build tag v10 (price moved to grid + custom dropdown UI)
//
// The build tag below is written into the DOM the instant this file is
// parsed so we can tell from the rendered page whether a stale cached
// copy is being served. If the compare modal shows "Loading..." but not
// this build tag, the browser/Shopify is still serving an old JS file.
// ====================================================================
window.__productCompareBuild = 'v52';
window.__productCompareDebug = { steps: [] };

/**
 * Publish anonymous compare events to:
 * 1) Shopify Customer Events (Settings → Customer events / custom pixels)
 * 2) GTM dataLayer (so GA4 tags can fire from existing GTM-WTBH7HF)
 *
 * Event names: compare_open | compare_select | compare_add_to_cart
 * Shopify names are prefixed: duffbeauty:compare_*
 */
const __pcTrack = (eventName, params = {}) => {
  try {
    const shortName = eventName.startsWith('duffbeauty:')
      ? eventName.slice('duffbeauty:'.length)
      : eventName;
    const shopifyName = `duffbeauty:${shortName}`;
    const payload = { ...params };

    if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
      Shopify.analytics.publish(shopifyName, payload);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: shortName, ...payload });
  } catch (_) {}
};
const __cmpDebug = (msg) => {
  try {
    window.__productCompareDebug.steps.push(msg);
    document.querySelectorAll('[data-compare-loading]').forEach(el => {
      let line = el.querySelector('[data-compare-debug-line]');
      if (!line) {
        line = document.createElement('p');
        line.setAttribute('data-compare-debug-line', '');
        line.style.cssText =
          'margin:.25rem 0 0;font-size:.6875rem;color:#bbb;text-align:center;font-family:system-ui,sans-serif;white-space:pre-wrap';
        el.appendChild(line);
      }
      line.textContent = window.__productCompareDebug.steps.join(' › ');
    });
  } catch (_) {}
};

(() => {
  // Paint a visible build tag over every loading placeholder immediately.
  // Works even if the custom element fails to upgrade later. Run as soon
  // as DOM is ready (script is deferred so the body is already parsed).
  const tagPlaceholders = () => {
    document.querySelectorAll('[data-compare-loading]').forEach(el => {
      const tag = document.createElement('p');
      tag.style.cssText =
        'margin:.5rem 0 0;font-size:.6875rem;color:#bbb;text-align:center;font-family:system-ui,sans-serif';
      tag.textContent = 'JS build: ' + window.__productCompareBuild;
      el.appendChild(tag);
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tagPlaceholders, { once: true });
  } else {
    tagPlaceholders();
  }

  __cmpDebug('script ran');

  /** Dropdowns only get a search field once the list is long enough that
   *  scrolling costs more than typing. Below this, search is just chrome.
   */
  const PC_SEARCH_THRESHOLD = 8;

  /** Stagger delay is 35ms per row, so uncapped it makes long lists take
   *  over a second to finish animating in. Cap the index instead.
   */
  const PC_STAGGER_CAP = 10;

  /** Phone-width layout, matching the mobile dropdown sizing rules. */
  const __pcIsNarrow = () => window.matchMedia('(max-width: 767px)').matches;

  /** Folds case and Danish diacritics so "olsen" matches "Ølsen" and
   *  "aben" matches "Åben" — otherwise search misses obvious hits.
   */
  const __pcFold = str =>
    String(str == null ? '' : str)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/æ/g, 'ae')
      .replace(/ø/g, 'o');

  /** Body + html helpers so the storefront never rubber-bands / scroll-
   *  chains underneath an open compare dialog (iOS Safari in particular).
   */
  const __pcLockBody = () => {
    document.documentElement.classList.add('product-compare-modal-open');
    document.body.classList.add('overflow-hidden');
  };
  const __pcUnlockBody = () => {
    document.documentElement.classList.remove('product-compare-modal-open');
    document.body.classList.remove('overflow-hidden');
  };

  /** Mobile bottom sheet max-height matches the *visible* viewport (iOS
   * bars / Visual Viewport API). Exported so both the CE & fallback open
   * paths can clamp the overflow-y region reliably.
   */
  const __pcMobileSheetMaxPx = () => {
    const v = window.visualViewport;
    const h = (v && v.height) || window.innerHeight || 600;
    return Math.max(220, Math.round(h * 0.92));
  };

  /** Pixels of the layout viewport covered by the on-screen keyboard.
   *  The sheet is anchored to `bottom: 0` of the layout viewport, which iOS
   *  does not move for the keyboard — so without this the sheet ends up
   *  behind it. Deltas under the threshold are browser chrome (URL bar
   *  collapsing), not a keyboard, and must not move the sheet.
   */
  const __pcKeyboardInsetPx = () => {
    const v = window.visualViewport;
    if (!v) return 0;
    const inset = Math.round((window.innerHeight || 0) - v.height - v.offsetTop);
    return inset > 80 ? inset : 0;
  };

  const __pcSyncCompareSheet = dialog => {
    if (!dialog || !dialog.open) return;
    const sheet = dialog.querySelector('.product-compare__dialog');
    if (!sheet) return;
    if (!window.matchMedia('(max-width: 749px)').matches) {
      sheet.style.maxHeight = '';
      sheet.style.transform = '';
      return;
    }
    const inset = __pcKeyboardInsetPx();
    sheet.style.transform = inset ? `translateY(-${inset}px)` : '';
    sheet.style.maxHeight = __pcMobileSheetMaxPx() + 'px';
  };

  const __pcOpenSourceFromAnchor = anchor => {
    if (!anchor || typeof anchor.closest !== 'function') return 'unknown';
    if (anchor.closest('.sizeguide-bar__compare')) return 'sizebar';
    if (anchor.closest('.product-compare__entry-button')) return 'entry';
    if (anchor.closest('[data-compare-open]')) return 'entry';
    return 'unknown';
  };

  /** Open path shared by onclick fallback, delegated capture listener, & CE internals. */
  const __pcOpenFromCompareHost = host => {
    if (!host) return;
    try {
      if (typeof host.open === 'function') {
        host.open();
        return;
      }
      const dlg = host.querySelector('dialog.product-compare__modal');
      if (dlg && typeof dlg.showModal === 'function' && !dlg.open) {
        dlg.showModal();
        __pcLockBody();
        __pcTrack('compare_open', {
          compare_source: host.__pcOpenSource || 'unknown',
          compare_page_path: window.location.pathname,
        });
        host.__pcOpenSource = null;
        requestAnimationFrame(() => {
          __pcSyncCompareSheet(dlg);
          requestAnimationFrame(() => __pcSyncCompareSheet(dlg));
        });
      }
    } catch (err) {
      console.error('[product-compare] open failed', err);
    }
  };

  window.productCompareTryOpen = anchor => {
    const host =
      (anchor && typeof anchor.closest === 'function' && anchor.closest('product-compare')) ||
      document.querySelector('product-compare');
    if (!host) return false;
    host.__pcOpenSource = __pcOpenSourceFromAnchor(anchor);
    __pcOpenFromCompareHost(host);
    return true;
  };

  if (!window.__productCompareViewportBound) {
    window.__productCompareViewportBound = true;
    const __pcSyncAllOpenSheets = () => {
      document.querySelectorAll('dialog.product-compare__modal[open]').forEach(d => {
        __pcSyncCompareSheet(d);
      });
    };
    window.addEventListener('resize', __pcSyncAllOpenSheets, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', __pcSyncAllOpenSheets, {
        passive: true,
      });
      window.visualViewport.addEventListener('scroll', __pcSyncAllOpenSheets, {
        passive: true,
      });
    }

    /* Focus changes drive the keyboard, and the viewport events that follow
     * can land before the keyboard has finished animating. Re-sync a couple
     * of times afterwards so the sheet settles in the right place.
     */
    const __pcSyncSoon = () => {
      __pcSyncAllOpenSheets();
      setTimeout(__pcSyncAllOpenSheets, 150);
      setTimeout(__pcSyncAllOpenSheets, 400);
    };
    document.addEventListener(
      'focusin',
      ev => {
        if (ev.target.closest && ev.target.closest('dialog.product-compare__modal')) {
          __pcSyncSoon();
        }
      },
      true
    );
    document.addEventListener('focusout', ev => {
      if (ev.target.closest && ev.target.closest('dialog.product-compare__modal')) {
        __pcSyncSoon();
      }
    }, true);
  }

  /* ESC / programmatic close MUST clear the locks even when the CE never
   * upgraded (only native <dialog> close runs). Capture phase catches
   * every compare modal instance. Safe to call __pcUnlockBody twice.
   */
  document.addEventListener(
    'close',
    ev => {
      const t = ev.target;
      if (t instanceof HTMLDialogElement && t.classList.contains('product-compare__modal')) {
        __pcUnlockBody();
        t.querySelectorAll('.product-compare__dialog').forEach(el => {
          el.style.maxHeight = '';
          el.style.transform = '';
        });
      }
    },
    true
  );

  // ------------------------------------------------------------------
  // Global click delegation: capture phase survives stopPropagation()
  // on bubbling paths from parent wrappers. Separate from define() —
  // this must run even when this file is bundled twice or the CE name
  // is already registered (multiple PDP snippets each inject the script tag).
  // ------------------------------------------------------------------
  if (!window.__productCompareDelegated) {
    window.__productCompareDelegated = true;

    document.addEventListener(
      'click',
      e => {
        const openBtn = e.target.closest('[data-compare-open]');
        if (openBtn) {
          const host =
            openBtn.closest('product-compare') || document.querySelector('product-compare');
          if (host) host.__pcOpenSource = __pcOpenSourceFromAnchor(openBtn);
          __pcOpenFromCompareHost(host);
          return;
        }
        const closeBtn = e.target.closest('[data-compare-close]');
        if (closeBtn) {
          const dialog = closeBtn.closest('dialog.product-compare__modal');
          if (dialog && typeof dialog.close === 'function') {
            dialog.close();
          }
        }
      },
      true
    );
  }

  class ProductCompare extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.config = {
        productSelectorLabel: 'Product',
        addToBagLabel: 'Add to cart',
        soldOutLabel: 'Sold out',
        unavailableLabel: 'Unavailable',
        searchPlaceholder: 'Search',
        searchPlaceholderShort: 'Search',
        searchLabel: 'Search',
        selectProductPrompt: 'Select product',
        emptyCompareNote: 'Select a product in the list to start comparing',
        ratingAria: '{{rating}} of {{rating_max}} stars',
        currentProductId: null,
      };
      this.columns = [null, null];
    }

    connectedCallback() {
      __cmpDebug('connected');
      console.log('[product-compare] connected', this);

      try {
        this._bootstrap();
      } catch (e) {
        __cmpDebug('FATAL ' + (e && e.message ? e.message : e));
        console.error('[product-compare] fatal error in connectedCallback', e);
        try { this._renderPayloadError(e); } catch (_) {}
      }
    }

    _bootstrap() {
      this.modal = this.querySelector('dialog.product-compare__modal');
      this.bodyEl = this.querySelector('[data-compare-body]');
      this.columnsEl = this.querySelector('.product-compare__columns');
      this.attributesEl = this.querySelector('section[data-compare-attributes]');
      __cmpDebug(
        'els ' +
          (this.modal ? 'm' : '-') +
          (this.bodyEl ? 'b' : '-') +
          (this.columnsEl ? 'c' : '-') +
          (this.attributesEl ? 'a' : '-')
      );
      this._bindTriggers();

      const productsScript = this.querySelector(
        'script[type="application/json"][data-compare-products]'
      );
      const configScript = this.querySelector(
        'script[type="application/json"][data-compare-config]'
      );

      if (!productsScript) {
        console.warn('[product-compare] missing [data-compare-products] script');
        this._renderPayloadError(new Error('No product data found'));
        return;
      }

      try {
        this.products = JSON.parse(productsScript.textContent);
        __cmpDebug('products ' + this.products.length);
        console.log('[product-compare] products loaded:', this.products.length, this.products);
      } catch (e) {
        __cmpDebug('products PARSE ERR ' + (e && e.message ? e.message : e));
        console.error('[product-compare] Failed to parse product payload', e);
        console.error('[product-compare] raw product payload:', productsScript.textContent);
        this._renderPayloadError(e);
        return;
      }

      if (!Array.isArray(this.products) || this.products.length === 0) {
        console.warn('[product-compare] product pool is empty');
        this._renderPayloadError(new Error('No products in pool'));
        return;
      }

      if (configScript) {
        try {
          Object.assign(this.config, JSON.parse(configScript.textContent));
          console.log('[product-compare] config loaded', this.config);
        } catch (e) {
          console.warn('[product-compare] Failed to parse config', e);
        }
      }

      const attributesJsonNode = this.querySelector(
        'script[type="application/json"][data-compare-attributes]'
      );
      this.compareData = { items: [], values: {} };
      if (attributesJsonNode) {
        try {
          this.compareData = JSON.parse(attributesJsonNode.textContent);
          console.log('[product-compare] attributes loaded', this.compareData);
        } catch (e) {
          console.warn('[product-compare] Failed to parse attributes payload', e);
          console.warn('[product-compare] raw attributes payload:', attributesJsonNode.textContent);
        }
      }

      try {
        this._initColumns();
        __cmpDebug('cols init');
        console.log('[product-compare] columns initialized', this.columns);
        this._renderAllColumns();
        __cmpDebug('render done');
        console.log('[product-compare] render complete');
      } catch (e) {
        __cmpDebug('render ERR ' + (e && e.message ? e.message : e));
        console.error('[product-compare] render failed', e);
        this._renderPayloadError(e);
      }
    }

    /**
     * Render an inline error band inside the dialog so failures are visible
     * to the merchant / developer instead of just a silent empty modal.
     */
    _renderPayloadError(err) {
      const msg = (err && err.message) || err || 'Unknown error';
      const html = `
        <div style="padding:1.5rem;border:1px solid #fbb;background:#fff5f5;color:#900;border-radius:4px;font-family:system-ui,sans-serif">
          <strong style="display:block;margin-bottom:.4rem">Compare data failed to load.</strong>
          <p style="margin:0;font-size:.85rem;white-space:pre-wrap">${msg}</p>
          <p style="margin:.5rem 0 0;font-size:.75rem;color:#666">Build: ${
            window.__productCompareBuild || '?'
          } — open the browser console for full details.</p>
        </div>
      `;
      const host = this.columnsEl || this.bodyEl;
      if (host) {
        host.innerHTML = html;
        return;
      }
      // Ultimate fallback: write into every column container we can find.
      this.querySelectorAll('[data-compare-column]').forEach(col => {
        col.innerHTML = html;
      });
    }

    /* ------------------------------------------------------------------ *
     * State initialization
     * ------------------------------------------------------------------ */

    _initColumns() {
      if (!this.products.length) return;

      const currentProduct = this._getCurrentProduct();
      if (currentProduct) {
        this.columns[0] = this._initialColumnState(currentProduct);
      }
    }

    _getCurrentProduct() {
      return (
        this.products.find(p => p.id === this.config.currentProductId) ||
        this.products[0]
      );
    }

    _initialColumnState(product) {
      const selectedOptions = {};
      (product.options_with_values || []).forEach(opt => {
        const firstAvailableVariant = product.variants.find(v => v.available);
        const sourceVariant = firstAvailableVariant || product.variants[0];
        if (sourceVariant && sourceVariant.options) {
          selectedOptions[opt.name] = sourceVariant.options[opt.position - 1];
        } else {
          selectedOptions[opt.name] = opt.values[0];
        }
      });
      return { productId: product.id, selectedOptions };
    }

    /* ------------------------------------------------------------------ *
     * Event binding
     * ------------------------------------------------------------------ */

    _bindTriggers() {
      this.querySelectorAll('[data-compare-open]').forEach(btn => {
        btn.addEventListener('click', () => {
          this.__pcOpenSource = __pcOpenSourceFromAnchor(btn);
          this.open();
        });
      });
      this.querySelectorAll('[data-compare-close]').forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Click-on-backdrop closes the dialog (clicks on the dialog element
      // itself, not its inner content, indicate a backdrop click).
      if (this.modal) {
        this.modal.addEventListener('click', e => {
          if (e.target === this.modal) this.close();
        });
      }
    }

    /* ------------------------------------------------------------------ *
     * Open / close
     * ------------------------------------------------------------------ */

    open() {
      if (!this.modal) return;
      const alreadyOpen = !!this.modal.open;
      if (typeof this.modal.showModal === 'function' && !this.modal.open) {
        this.modal.showModal();
      }
      __pcLockBody();
      if (!alreadyOpen) {
        const current =
          this.products.find(p => p.id === this.config.currentProductId) ||
          (this.columns[0] && this.products.find(p => p.id === this.columns[0].productId));
        __pcTrack('compare_open', {
          compare_source: this.__pcOpenSource || 'entry',
          compare_page_path: window.location.pathname,
          compare_product_id: current ? current.id : null,
          compare_product_title: current ? current.title : null,
          compare_product_handle: current ? current.handle : null,
        });
        this.__pcOpenSource = null;
      }
      requestAnimationFrame(() => {
        __pcSyncCompareSheet(this.modal);
        requestAnimationFrame(() => __pcSyncCompareSheet(this.modal));
      });
    }

    close() {
      if (!this.modal) return;
      if (typeof this.modal.close === 'function' && this.modal.open) {
        this.modal.close();
      }
    }

    /* ------------------------------------------------------------------ *
     * Rendering
     * ------------------------------------------------------------------ */

    _renderAllColumns() {
      // Pad-count for the summary row: ensures the summary cell is the same
      // height across both columns even when one product has fewer variant
      // options than the other (subgrid takes care of column alignment, but
      // visual balance still benefits from matching line counts).
      this._maxOptionCount = this.columns.reduce((max, state) => {
        if (!state) return max;
        const product = this.products.find(p => p.id === state.productId);
        const n = (product && product.options_with_values && product.options_with_values.length) || 0;
        return Math.max(max, n);
      }, 0);

      this.columns.forEach((_, idx) => this._renderColumn(idx));
      this._renderAttributesSection();
      __pcSyncCompareSheet(this.modal);
    }

    _renderColumn(idx) {
      const container = this.querySelector(`[data-compare-column="${idx}"]`);
      if (!container) return;

      const state = this.columns[idx];
      if (!state) {
        container.innerHTML = idx === 1 ? this._renderEmptyComparisonColumn(idx) : '';
        this._bindColumnListeners(container, idx);
        this._renderAttributesSection();
        return;
      }

      const product = this.products.find(p => p.id === state.productId);
      if (!product) {
        container.innerHTML = '';
        this._renderAttributesSection();
        return;
      }

      const variant = this._resolveVariant(product, state.selectedOptions);
      const image = (variant && variant.featured_image) || product.featured_image;

      const variantSelectorsHtml = this._renderVariantSelectorsPadded(
        product,
        idx,
        state
      );

      const styleTitleHtml = this._renderStyleTitle(idx, product);

      const addDisabled = !variant || !variant.available;
      const addLabel = !variant
        ? this.config.unavailableLabel
        : !variant.available
        ? this.config.soldOutLabel
        : this.config.addToBagLabel;

      const productIntroHtml = this._renderProductIntro(product, variant);
      const sizebarInlineHtml = this._renderInlineSizebar(product);

      container.innerHTML = `
        <div class="product-compare__row product-compare__row--style">
          ${styleTitleHtml}
        </div>

        <div class="product-compare__row product-compare__row--variants">
          ${variantSelectorsHtml}
        </div>

        <div class="product-compare__row product-compare__row--details">
          ${productIntroHtml}
        </div>

        <div class="product-compare__row product-compare__row--image">
          <div class="product-compare__image-wrap">
            ${
              image
                ? `<img src="${image}" alt="${this._escape(product.title)}" loading="lazy" class="product-compare__image" />`
                : '<div class="product-compare__image"></div>'
            }
          </div>
        </div>

        <div class="product-compare__row product-compare__row--button">
          <button
            type="button"
            class="product-compare__add-button product-form__submit button button--filled"
            data-compare-add
            data-column="${idx}"
            ${addDisabled ? 'disabled' : ''}
          >
            <span>${this._escape(addLabel)}</span>
          </button>
        </div>

        <div class="product-compare__row product-compare__row--sizebar">
          ${sizebarInlineHtml}
        </div>
      `;

      this._bindColumnListeners(container, idx);
    }

    _renderEmptyComparisonColumn(idx) {
      const placeholder = this.config.selectProductPrompt || this.config.productSelectorLabel;
      const items = [
        {
          value: '',
          label: placeholder,
          selected: true,
          disabled: true,
        },
        ...this._groupedProductItems(null),
      ];

      return `
        <div class="product-compare__row product-compare__row--style">
          <div class="product-compare__style-field">
            <span class="product-compare__style-label">${this._escape(
              this.config.productSelectorLabel
            )}</span>
            ${this._renderDropdown({
              items,
              ariaLabel: this.config.productSelectorLabel,
              selectClass: 'product-compare__style-select',
              wrapperClass: 'product-compare__style-control pc-dropdown pc-dropdown--style',
              selectAttrs: `data-compare-product-select data-column="${idx}"`,
              searchable: true,
            })}
          </div>
        </div>
        <div class="product-compare__row product-compare__row--variants product-compare__row--empty">
          <p class="product-compare__empty-note">${this._escape(
            this.config.emptyCompareNote || this.config.selectProductPrompt || ''
          )}</p>
        </div>
      `;
    }

    /**
     * Premium product intro: title, rating and price are intentionally
     * higher in the hierarchy than form controls and table values.
     */
    _renderProductIntro(product, variant) {
      return `
        <div class="product-compare__product-heading">
          <div class="product-compare__product-copy">
            <h3 class="product-compare__product-title">${this._escape(product.title)}</h3>
            ${this._renderRating(product)}
            ${this._renderInlinePrice(variant)}
          </div>
        </div>
      `;
    }

    _renderRating(product) {
      const rating = Number(product && product.rating);
      if (!rating || Number.isNaN(rating)) return '';

      const ratingMax = 5;
      const fullStars = Math.max(0, Math.min(ratingMax, Math.floor(rating)));
      const halfStar = rating - fullStars >= 0.5 && fullStars < ratingMax ? 1 : 0;
      const emptyStars = Math.max(0, ratingMax - fullStars - halfStar);
      const stars =
        '★'.repeat(fullStars) + (halfStar ? '★' : '') + '☆'.repeat(emptyStars);
      const count = product.rating_count ? ` (${this._escape(product.rating_count)})` : '';

      const ratingAria = (this.config.ratingAria || '{{rating}} / {{rating_max}}')
        .split('{{rating}}').join(String(rating))
        .split('{{rating_max}}').join(String(ratingMax));
      return `
        <div class="product-compare__rating" aria-label="${this._escape(
          `${ratingAria}${count}`
        )}">
          <span aria-hidden="true">${stars}</span>
          ${count ? `<span class="product-compare__rating-count">${count}</span>` : ''}
        </div>
      `;
    }

    /**
     * Price is a hero element in the card. Compare-at price is kept small
     * and warm so savings read as a subtle beauty-brand accent.
     */
    _renderInlinePrice(variant) {
      if (!variant) {
        return '<span class="product-compare__inline-price product-compare__inline-price--empty">—</span>';
      }
      const hasCompare =
        variant.compare_at_price && variant.compare_at_price !== variant.price;
      const compareHtml = hasCompare
        ? `<span class="product-compare__inline-price-strike">${this._escape(
            variant.compare_at_price
          )}</span>`
        : '';
      return `
        <div class="product-compare__inline-price-wrap">
          <span class="product-compare__inline-price">${this._escape(variant.price)}</span>
          ${compareHtml ? `<span class="product-compare__saving">${compareHtml}</span>` : ''}
        </div>
      `;
    }

    /**
     * Inline size bar below the price. Conditional — if the product has no
     * size metafield value the entire block is skipped so the row collapses.
     */
    _renderInlineSizebar(product) {
      const steps = ['extra-small', 'small', 'medium', 'large', 'extra-large'];
      const sizeKey = product && product.size_key;
      if (!sizeKey || !steps.includes(sizeKey)) return '';
      const cfg = (this.config && this.config.sizebar) || {};
      const volumeLabel = cfg.title || 'Volume';
      return `
        <div class="product-compare__volume-field">
          <span class="product-compare__volume-label">${this._escape(volumeLabel)}</span>
          ${this._renderSizebar(product)}
        </div>
      `;
    }

    /**
     * The "Style" field is the column's product title — a small caption
     * above a large bold dropdown that displays
     * the product's name. Functions as a product switcher; the chevron
     * affords interactivity without a heavy dropdown frame.
     */
    _renderStyleTitle(idx, product) {
      return `
        <div class="product-compare__style-field">
          <span class="product-compare__style-label">${this._escape(
            this.config.productSelectorLabel
          )}</span>
          ${this._renderDropdown({
            items: this._groupedProductItems(product.id),
            ariaLabel: this.config.productSelectorLabel,
            selectClass: 'product-compare__style-select',
            wrapperClass: 'product-compare__style-control pc-dropdown pc-dropdown--style',
            selectAttrs: `data-compare-product-select data-column="${idx}"`,
            searchable: true,
          })}
        </div>
      `;
    }

    /**
     * Product picker items ordered so products sharing a collection sit
     * together — the dropdown emits a heading each time the group changes,
     * so unsorted input would produce repeated headings.
     */
    _groupedProductItems(selectedId) {
      const groups = [];
      this.products.forEach(p => {
        const group = p.group || '';
        if (!groups.includes(group)) groups.push(group);
      });

      return groups.flatMap(group =>
        this.products
          .filter(p => (p.group || '') === group)
          .map(p => ({
            value: String(p.id),
            label: p.title,
            group,
            selected: p.id === selectedId,
            disabled: false,
          }))
      );
    }

    /**
     * Renders Shopify option dropdowns padded to `_maxOptionCount` so columns
     * with fewer options reserve the same vertical space (images + CTAs align).
     */
    _renderVariantSelectorsPadded(product, idx, state) {
      const parts = (product.options_with_values || []).map(opt =>
        this._renderOptionSelect(idx, product, opt, state)
      );
      const pad = Math.max(0, (this._maxOptionCount || 0) - parts.length);
      for (let i = 0; i < pad; i++) {
        parts.push(
          '<div class="product-compare__variant-placeholder" aria-hidden="true"></div>'
        );
      }
      return parts.join('');
    }

    _renderOptionSelect(idx, product, option, state) {
      const currentValue = state.selectedOptions[option.name];
      const optionValues = Array.isArray(option.values) ? option.values : [];

      // If there is only one possible value, show it as plain text without
      // a duplicated option title/dropdown chrome.
      if (optionValues.length <= 1) {
        const singleValue = currentValue || optionValues[0] || '—';
        return `
          <div class="product-compare__field product-compare__field--single">
            <span class="product-compare__field-value">${this._escape(singleValue)}</span>
          </div>
        `;
      }

      const items = optionValues.map(v => {
        const isAvailable = this._isOptionValueAvailable(product, option, v, state);
        return {
          value: v,
          label: isAvailable ? v : `${v} - ${this.config.soldOutLabel}`,
          selected: v === currentValue,
          disabled: !isAvailable,
        };
      });

      return `
        <div class="product-compare__field">
          <span class="product-compare__field-label">${this._escape(option.name)}</span>
          ${this._renderDropdown({
            items,
            ariaLabel: option.name,
            selectClass: 'product-compare__select',
            wrapperClass: 'product-compare__select-wrap pc-dropdown',
            selectAttrs: `data-compare-option-select data-column="${idx}" data-option-name="${this._escape(option.name)}"`,
          })}
        </div>
      `;
    }

    /**
     * Shared markup builder for our custom animated dropdown. Each dropdown
     * keeps a real <select> in the DOM (visually hidden) so that the existing
     * change-event listeners keep working and screen readers / form state
     * still operate on a real form control. A custom trigger button + panel
     * sits on top with smooth open/close animations and animated chevron.
     */
    _renderDropdown({
      items,
      ariaLabel,
      selectClass,
      wrapperClass,
      selectAttrs,
      searchable = false,
    }) {
      const selected = items.find(it => it.selected) || items[0] || { value: '', label: '' };
      const withSearch = searchable && items.length > PC_SEARCH_THRESHOLD;

      const optionsHtml = items
        .map(
          it =>
            `<option value="${this._escape(it.value)}" ${it.selected ? 'selected' : ''} ${
              it.disabled ? 'disabled' : ''
            }>${this._escape(it.label)}</option>`
        )
        .join('');

      // Group headings are emitted inline whenever the group changes, so the
      // list keeps a single flat scroll container while still reading as
      // sections. Blank groups simply produce no heading.
      let lastGroup = null;
      const panelOptionsHtml = items
        .map((it, i) => {
          let headingHtml = '';
          const group = it.group || '';
          if (group !== lastGroup && group !== '') {
            headingHtml = `
            <li class="pc-dropdown__group" role="presentation" data-pc-group data-group="${this._escape(
              group
            )}">${this._escape(group)}</li>`;
          }
          lastGroup = group;

          return `${headingHtml}
            <li
              class="pc-dropdown__option ${it.selected ? 'is-selected' : ''} ${
                it.disabled ? 'is-disabled' : ''
              }"
              role="option"
              aria-selected="${it.selected ? 'true' : 'false'}"
              aria-disabled="${it.disabled ? 'true' : 'false'}"
              data-pc-option
              data-value="${this._escape(it.value)}"
              data-index="${i}"
              data-search="${this._escape(__pcFold(`${it.label} ${group}`))}"
              tabindex="-1"
              style="--pc-stagger:${Math.min(i, PC_STAGGER_CAP)}"
            >
              <span class="pc-dropdown__option-label">${this._escape(it.label)}</span>
              <svg class="pc-dropdown__option-check" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M3 8.5l3.2 3.2L13 5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </li>`;
        })
        .join('');

      const searchHtml = withSearch
        ? `
          <div class="pc-dropdown__search">
            <svg class="pc-dropdown__search-icon" viewBox="0 0 16 16" aria-hidden="true">
              <circle cx="7" cy="7" r="4.25" fill="none" stroke="currentColor" stroke-width="1.5"/>
              <path d="M10.2 10.2L14 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <input
              type="text"
              class="pc-dropdown__search-input"
              data-pc-search
              placeholder="${this._escape(
                __pcIsNarrow()
                  ? this.config.searchPlaceholderShort || this.config.searchLabel || this.config.searchPlaceholder
                  : this.config.searchPlaceholder || this.config.searchLabel
              )}"
              aria-label="${this._escape(
                `${ariaLabel} - ${this.config.searchLabel || this.config.searchPlaceholderShort || this.config.searchPlaceholder}`
              )}"
              autocomplete="off"
              spellcheck="false"
            />
          </div>`
        : '';

      return `
        <div class="${wrapperClass}" data-pc-dropdown>
          <button
            type="button"
            class="pc-dropdown__trigger"
            data-pc-trigger
            aria-haspopup="listbox"
            aria-expanded="false"
            aria-label="${this._escape(ariaLabel)}"
          >
            <span class="pc-dropdown__value" data-pc-value>${this._escape(selected.label)}</span>
            <svg class="pc-dropdown__chevron" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="pc-dropdown__panel" data-pc-panel>
            ${searchHtml}
            <ul
              class="pc-dropdown__list"
              role="listbox"
              data-pc-list
              aria-label="${this._escape(ariaLabel)}"
              tabindex="-1"
            >
              ${panelOptionsHtml}
            </ul>
            <p class="pc-dropdown__empty" data-pc-empty hidden>Ingen resultater</p>
          </div>
          <select
            class="${selectClass} pc-dropdown__native"
            ${selectAttrs}
            aria-hidden="true"
            tabindex="-1"
          >
            ${optionsHtml}
          </select>
        </div>
      `;
    }

    /**
     * Compact size bar used as a value inside the attributes section — the
     * attribute header already says "Size", so we omit the internal title
     * and only render the 5-segment gauge plus small/regular/large scale
     * labels. If the product has no size metafield, render a dash so the
     * row still has content aligned with the other column.
     */
    _renderSizebar(product) {
      const steps = ['extra-small', 'small', 'medium', 'large', 'extra-large'];
      const sizeKey = product && product.size_key;
      const cfg = (this.config && this.config.sizebar) || {};

      if (!sizeKey || !steps.includes(sizeKey)) {
        return '<span class="product-compare__attr-value product-compare__attr-value--empty">—</span>';
      }

      const bars = steps
        .map(
          step =>
            `<span class="product-compare__sizebar-step" data-active="${
              step === sizeKey ? 'true' : 'false'
            }"></span>`
        )
        .join('');

      return `
        <div class="product-compare__sizebar">
          <div class="product-compare__sizebar-steps" aria-hidden="true">${bars}</div>
          <div class="product-compare__sizebar-scale">
            <span>${this._escape(cfg.small || 'Small')}</span>
            <span>${this._escape(cfg.regular || 'Regular')}</span>
            <span>${this._escape(cfg.large || 'Large')}</span>
          </div>
        </div>
      `;
    }

    /* ------------------------------------------------------------------ *
     * Comparison attributes section
     *
     * Renders the merchant-configured attribute rows below the two product
     * cards. Each row is a labeled band spanning the full width, with the
     * two products' values split 1fr / 1fr beneath the heading — the
     * "highlight what's different" focal point of the modal.
     *
     * The row set comes entirely from the Liquid payload, which is the
     * single source of truth for metafield keys.
     * ------------------------------------------------------------------ */

    _renderAttributesSection() {
      if (!this.attributesEl) return;
      const rawItems = (this.compareData && this.compareData.items) || [];
      // Price and volume are hero elements in the product cards, so keep
      // the table focused on attributes like color, longevity and glue.
      const filtered = rawItems.filter(item => {
        const source = String(item.source || '').toLowerCase();
        const label = String(item.label || '').toLowerCase();
        const isCardOnly =
          source === '__size__' || source === '__price__' || source === '__volume__';
        const isLongText = /description|beskrivelse|body|summary|resume|tekst/.test(
          `${source} ${label}`
        );
        return !isCardOnly && !isLongText;
      });

      if (!filtered.length) {
        this.attributesEl.innerHTML = '';
        return;
      }

      const rowsHtml = filtered
        .map(item => this._renderAttributeRow(item))
        .join('');

      // Each row has a dedicated label column plus a product value track.
      // On mobile the whole table scrolls horizontally while row labels
      // stay sticky, so the compared values keep enough room to breathe.
      this.attributesEl.innerHTML = `
        <div class="product-compare__compare">
          <div class="product-compare__compare-grid">
            ${rowsHtml}
          </div>
        </div>
      `;
    }

    _renderAttributeRow(item) {
      const label = item.label || '';
      const source = item.source || '';

      const values = this.columns.map(state => {
        if (!state) return this._renderAttributeValue(source, null, null);
        const product = this.products.find(p => p.id === state.productId);
        if (!product) return this._renderAttributeValue(source, null, null);
        const variant = this._resolveVariant(product, state.selectedOptions);
        return this._renderAttributeValue(source, product, variant);
      });
      const isDifferent =
        this._normalizeCompareValue(values[0]) !== this._normalizeCompareValue(values[1]);
      const cellClass = isDifferent
        ? 'product-compare__compare-cell product-compare__compare-cell--different'
        : 'product-compare__compare-cell';

      return `
        <div class="product-compare__compare-row" data-source="${this._escape(source)}">
          ${
            label
              ? `<div class="product-compare__compare-rowlabel">${this._escape(label)}</div>`
              : ''
          }
          <div class="product-compare__compare-values">
            <div class="${cellClass}">${values[0]}</div>
            <div class="${cellClass}">${values[1]}</div>
          </div>
        </div>
      `;
    }

    _renderAttributeValue(source, product, variant) {
      if (!product) {
        return '<span class="product-compare__attr-value product-compare__attr-value--empty">—</span>';
      }

      if (source === '__size__') {
        return this._renderSizebar(product);
      }

      // Metafield lookup by namespace.key — value was baked into the
      // attributes JSON payload server-side (see snippets/product-compare).
      // Keys are stringified product IDs so the JSON stays valid.
      const valuesMap = (this.compareData && this.compareData.values) || {};
      const byProduct = valuesMap[product.id] || valuesMap[String(product.id)] || {};
      const raw = byProduct[source];
      if (raw == null || raw === '') {
        return '<span class="product-compare__attr-value product-compare__attr-value--empty">—</span>';
      }
      return `<span class="product-compare__attr-value">${this._escape(raw)}</span>`;
    }


    _bindColumnListeners(container, idx) {
      container
        .querySelector('[data-compare-product-select]')
        ?.addEventListener('change', e => {
          const newProductId = Number(e.target.value);
          const product = this.products.find(p => p.id === newProductId);
          if (!product) return;
          this.columns[idx] = this._initialColumnState(product);
          __pcTrack('compare_select', {
            compare_select_type: 'product',
            compare_column: idx,
            compare_product_id: product.id,
            compare_product_title: product.title,
            compare_product_handle: product.handle,
            compare_page_path: window.location.pathname,
          });
          this._renderAllColumns(); // re-render both: option counts may differ
        });

      container.querySelectorAll('[data-compare-option-select]').forEach(sel => {
        sel.addEventListener('change', e => {
          const optionName = e.target.dataset.optionName;
          const value = e.target.value;
          this.columns[idx].selectedOptions[optionName] = value;
          const product = this.products.find(p => p.id === this.columns[idx].productId);
          __pcTrack('compare_select', {
            compare_select_type: 'option',
            compare_column: idx,
            compare_option_name: optionName,
            compare_option_value: value,
            compare_product_id: product ? product.id : null,
            compare_product_title: product ? product.title : null,
            compare_product_handle: product ? product.handle : null,
            compare_page_path: window.location.pathname,
          });
          this._renderColumn(idx);
          // Price (and any variant-derived attribute) needs to re-compute
          // whenever the variant changes.
          this._renderAttributesSection();
        });
      });

      container
        .querySelector('[data-compare-add]')
        ?.addEventListener('click', () => this._addToCart(idx));

      this._enhanceDropdowns(container);
    }

    /**
     * Wires up the custom animated dropdown UI for every [data-pc-dropdown]
     * inside the given container. The native <select> remains the source of
     * truth — clicking a custom option simply sets select.value and dispatches
     * a change event so the existing variant/product listeners keep working.
     */
    _enhanceDropdowns(container) {
      container.querySelectorAll('[data-pc-dropdown]').forEach(root => {
        if (root.__pcEnhanced) return;
        root.__pcEnhanced = true;

        const trigger = root.querySelector('[data-pc-trigger]');
        const panel = root.querySelector('[data-pc-panel]');
        const list = root.querySelector('[data-pc-list]') || panel;
        const search = root.querySelector('[data-pc-search]');
        const emptyEl = root.querySelector('[data-pc-empty]');
        const groups = Array.from(root.querySelectorAll('[data-pc-group]'));
        const valueEl = root.querySelector('[data-pc-value]');
        const native = root.querySelector('select');
        const options = Array.from(root.querySelectorAll('[data-pc-option]'));
        if (!trigger || !panel || !native) return;

        /** Options that can currently be reached by keyboard or click. */
        const selectable = () =>
          options.filter(o => !o.hidden && !o.classList.contains('is-disabled'));

        /** Hides non-matching options, then hides any heading left without
         *  visible options beneath it. Empty query restores everything.
         */
        const applyFilter = query => {
          // Every whitespace-separated token must match, so word order in the
          // query doesn't matter ("premium wispy" finds "Wispy").
          const tokens = __pcFold(query).split(/\s+/).filter(Boolean);
          options.forEach(o => {
            const haystack = o.dataset.search || '';
            o.hidden = !tokens.every(t => haystack.includes(t));
          });

          groups.forEach(heading => {
            let hasVisible = false;
            let node = heading.nextElementSibling;
            while (node && !node.hasAttribute('data-pc-group')) {
              if (node.hasAttribute('data-pc-option') && !node.hidden) {
                hasVisible = true;
                break;
              }
              node = node.nextElementSibling;
            }
            heading.hidden = !hasVisible;
          });

          if (emptyEl) emptyEl.hidden = options.some(o => !o.hidden);
        };

        const resetFilter = () => {
          if (!search) return;
          search.value = '';
          applyFilter('');
          list.scrollTop = 0;
        };

        const close = () => {
          if (!root.classList.contains('is-open')) return;
          root.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
          resetFilter();
        };
        const open = () => {
          // Close any other open dropdowns in the same modal first
          if (this.modal) {
            this.modal.querySelectorAll('[data-pc-dropdown].is-open').forEach(other => {
              if (other !== root) {
                other.classList.remove('is-open');
                other.querySelector('[data-pc-trigger]')?.setAttribute('aria-expanded', 'false');
              }
            });
          }
          root.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          // Typing is the fastest path on desktop, so the search field takes
          // focus. On phones that would summon the keyboard unasked and shove
          // the sheet around, so there the user taps the field themselves.
          if (search && !__pcIsNarrow()) {
            requestAnimationFrame(() => search.focus({ preventScroll: true }));
            return;
          }
          const selected = root.querySelector('[data-pc-option].is-selected') || options[0];
          if (selected) {
            requestAnimationFrame(() => selected.focus({ preventScroll: true }));
          }
        };
        const toggle = () => (root.classList.contains('is-open') ? close() : open());

        const choose = optionEl => {
          if (!optionEl || optionEl.classList.contains('is-disabled')) return;
          const value = optionEl.dataset.value;
          options.forEach(o => {
            const isThis = o === optionEl;
            o.classList.toggle('is-selected', isThis);
            o.setAttribute('aria-selected', isThis ? 'true' : 'false');
          });
          if (valueEl) valueEl.textContent = optionEl.querySelector('.pc-dropdown__option-label')?.textContent || '';
          if (native.value !== value) {
            native.value = value;
            native.dispatchEvent(new Event('change', { bubbles: true }));
          }
          close();
          trigger.focus({ preventScroll: true });
        };

        trigger.addEventListener('click', e => {
          e.stopPropagation();
          toggle();
        });

        // Prevent wheel-scroll chaining from the options list into the
        // modal/page scroll container when users hit list boundaries.
        list.addEventListener(
          'wheel',
          e => {
            if (!root.classList.contains('is-open')) return;
            const dy = e.deltaY || 0;
            if (!dy) return;
            const atTop = list.scrollTop <= 0;
            const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
            if ((dy < 0 && atTop) || (dy > 0 && atBottom)) {
              e.preventDefault();
            }
            e.stopPropagation();
          },
          { passive: false }
        );

        if (search) {
          search.addEventListener('click', e => e.stopPropagation());
          search.addEventListener('input', () => {
            applyFilter(search.value);
            list.scrollTop = 0;
          });
          search.addEventListener('keydown', e => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              selectable()[0]?.focus({ preventScroll: true });
            } else if (e.key === 'Enter') {
              // Enter on a single remaining match is the fast path people
              // expect from a type-to-find field.
              e.preventDefault();
              const matches = selectable();
              if (matches.length) choose(matches[0]);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              close();
              trigger.focus({ preventScroll: true });
            }
          });
        }

        options.forEach(opt => {
          opt.addEventListener('click', e => {
            e.stopPropagation();
            choose(opt);
          });
          opt.addEventListener('keydown', e => {
            const visible = selectable();
            const idx = visible.indexOf(opt);
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              const next = visible[(idx + 1) % visible.length];
              next?.focus({ preventScroll: true });
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              if (search && idx === 0) {
                search.focus({ preventScroll: true });
                return;
              }
              const prev = visible[(idx - 1 + visible.length) % visible.length];
              prev?.focus({ preventScroll: true });
            } else if (e.key === 'Home') {
              e.preventDefault();
              visible[0]?.focus({ preventScroll: true });
            } else if (e.key === 'End') {
              e.preventDefault();
              visible[visible.length - 1]?.focus({ preventScroll: true });
            } else if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              choose(opt);
            } else if (e.key === 'Escape' || e.key === 'Tab') {
              close();
              trigger.focus({ preventScroll: true });
            }
          });
        });

        trigger.addEventListener('keydown', e => {
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        });

        // Close when clicking outside this dropdown
        document.addEventListener(
          'click',
          e => {
            if (!root.contains(e.target)) close();
          },
          true
        );
      });
    }

    /* ------------------------------------------------------------------ *
     * Variant resolution
     * ------------------------------------------------------------------ */

    _resolveVariant(product, selectedOptions) {
      const orderedOptions = (product.options_with_values || [])
        .slice()
        .sort((a, b) => a.position - b.position)
        .map(o => selectedOptions[o.name]);

      return product.variants.find(v => {
        if (!v.options || v.options.length !== orderedOptions.length) return false;
        return v.options.every((val, i) => val === orderedOptions[i]);
      });
    }

    _isOptionValueAvailable(product, option, value, state) {
      const testSelection = { ...state.selectedOptions, [option.name]: value };
      const orderedOptions = (product.options_with_values || [])
        .slice()
        .sort((a, b) => a.position - b.position)
        .map(o => testSelection[o.name]);

      const match = product.variants.find(v => {
        if (!v.options || v.options.length !== orderedOptions.length) return false;
        return v.options.every((val, i) => val === orderedOptions[i]);
      });

      return !!(match && match.available);
    }

    /* ------------------------------------------------------------------ *
     * Add to cart
     * ------------------------------------------------------------------ */

    _addToCart(idx) {
      const state = this.columns[idx];
      if (!state) return;
      const product = this.products.find(p => p.id === state.productId);
      if (!product) return;
      const variant = this._resolveVariant(product, state.selectedOptions);
      if (!variant || !variant.available) return;

      const button = this.querySelector(`[data-compare-add][data-column="${idx}"]`);
      button?.classList.add('disabled');
      button?.setAttribute('disabled', 'disabled');

      const cartDrawer = document.querySelector('cart-drawer');

      const formData = new FormData();
      formData.append('id', variant.id);
      formData.append('quantity', 1);

      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          cartDrawer.getSectionsToRender().map(section => section.section).join(',')
        );
        formData.append('sections_url', window.location.pathname);
      }

      const config =
        typeof fetchConfig === 'function'
          ? fetchConfig('javascript')
          : {
              method: 'POST',
              headers: {
                Accept: 'application/javascript',
                'X-Requested-With': 'XMLHttpRequest',
              },
            };
      if (config.headers) {
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
      }
      config.body = formData;

      const cartAddUrl =
        (typeof routes !== 'undefined' && routes.cart_add_url) || '/cart/add.js';

      fetch(cartAddUrl, config)
        .then(res => res.json())
        .then(response => {
          if (response.status) {
            console.warn('[product-compare] add to cart failed', response);
            return;
          }
          __pcTrack('compare_add_to_cart', {
            compare_column: idx,
            compare_product_id: product.id,
            compare_product_title: product.title,
            compare_product_handle: product.handle,
            compare_variant_id: variant.id,
            compare_variant_title: variant.title,
            compare_page_path: window.location.pathname,
          });
          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(response);
          }
          const cartDrawerCounter = document.querySelector('.cart-drawer__title-counter');
          if (cartDrawerCounter) {
            const counterValue = parseInt(cartDrawerCounter.innerHTML, 10) || 0;
            cartDrawerCounter.innerHTML = counterValue + 1;
          }
          this.close();
        })
        .catch(err => {
          console.error('[product-compare] add to cart error', err);
        })
        .finally(() => {
          button?.classList.remove('disabled');
          button?.removeAttribute('disabled');
        });
    }

    /* ------------------------------------------------------------------ *
     * Utils
     * ------------------------------------------------------------------ */

    _normalizeCompareValue(html) {
      if (html == null) return '';
      return String(html)
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    }

    _escape(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  if (!customElements.get('product-compare')) {
    __cmpDebug('defining CE');
    customElements.define('product-compare', ProductCompare);
  } else {
    __cmpDebug('CE already defined, skipped define');
  }
})();
